import type { ToolCategory, ToolDefinition } from './types';
import type { ToolUsage } from './usage';

export interface FileDescriptor {
  name: string;
  type: string;
}

const nextStepIds: Partial<Record<ToolCategory, string[]>> = {
  pdf: ['pdf-compress', 'pdf-to-image', 'pdf-privacy', 'pdf-signature'],
  image: ['image-compress', 'image-convert', 'ocr', 'pdf-create'],
  table: ['table-cleaner', 'excel-formula-helper', 'batch-docs', 'qr-code'],
  text: ['text-cleaner', 'markdown-preview', 'html-markdown', 'pdf-create'],
  data: ['data-format-converter', 'json-query', 'table-cleaner', 'openapi-workbench'],
  developer: ['data-codec', 'code-formatter', 'json-query', 'api-request'],
  network: ['api-request', 'websocket-client', 'mqtt-client', 'dns-mail-security'],
  generator: ['qr-code', 'barcode', 'batch-docs', 'secure-generator'],
  design: ['social-image-studio', 'image-convert', 'favicon-generator', 'svg-workbench'],
  video: ['media-studio', 'subtitle-converter', 'screen-recorder', 'audio-workbench'],
  audio: ['audio-workbench', 'media-studio', 'subtitle-converter', 'waveform'],
  security: ['crypto-suite', 'password-hash', 'file-hash', 'frontend-security'],
  daily: ['date-calculator', 'unit-converter', 'calendar-event', 'health-calculator'],
  random: ['random-picker', 'mock-data', 'qr-code', 'batch-docs'],
};

export function recommendToolsForFiles(files: readonly FileDescriptor[], items: readonly ToolDefinition[], limit = 5): ToolDefinition[] {
  if (!files.length) return [];
  return items
    .map((tool, index) => ({ tool, index, score: files.reduce((total, file) => total + fileMatchScore(file, tool), 0) }))
    .filter((entry) => entry.score > 0 && entry.tool.status === 'available')
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .slice(0, limit)
    .map((entry) => entry.tool);
}

export function selectQuickAccess(items: readonly ToolDefinition[], usage: ToolUsage, preferredIds: readonly string[], limit = 6): ToolDefinition[] {
  const recentRank = new Map(usage.recent.map((id, index) => [id, index]));
  const favoriteIds = new Set(usage.favorites);
  const personalized = items
    .filter((tool) => (usage.counts[tool.parentToolId || tool.id] || 0) > 0 || favoriteIds.has(tool.parentToolId || tool.id))
    .sort((left, right) => {
      const leftId = left.parentToolId || left.id;
      const rightId = right.parentToolId || right.id;
      return (usage.counts[rightId] || 0) - (usage.counts[leftId] || 0)
        || Number(favoriteIds.has(rightId)) - Number(favoriteIds.has(leftId))
        || (recentRank.get(leftId) ?? Number.MAX_SAFE_INTEGER) - (recentRank.get(rightId) ?? Number.MAX_SAFE_INTEGER);
    });
  const preferred = preferredIds.map((id) => items.find((tool) => tool.id === id)).filter((tool): tool is ToolDefinition => Boolean(tool));
  return uniqueTools([...personalized, ...preferred], limit);
}

export function selectNextStepTools(definition: ToolDefinition, items: readonly ToolDefinition[], limit = 4): ToolDefinition[] {
  const currentId = definition.parentToolId || definition.id;
  const specificNextSteps: Record<string, string[]> = {
    'data-chart': ['table-cleaner', 'sql-workbench', 'image-convert', 'docx-template'],
    'docx-template': ['table-cleaner', 'batch-docs', 'qr-code', 'data-chart'],
    'log-analyzer': ['sql-workbench', 'har-viewer', 'regex-tester', 'api-request'],
    'sql-workbench': ['data-chart', 'table-cleaner', 'data-format-converter', 'log-analyzer'],
    'table-cleaner': ['data-chart', 'docx-template', 'sql-workbench', 'qr-code'],
  };
  const preferred = (specificNextSteps[currentId] || nextStepIds[definition.category] || [])
    .map((id) => items.find((tool) => tool.id === id))
    .filter((tool): tool is ToolDefinition => tool !== undefined && tool.id !== currentId);
  const sameCategory = items.filter((tool) => tool.category === definition.category && tool.id !== currentId);
  return uniqueTools([...preferred, ...sameCategory], limit);
}

function fileMatchScore(file: FileDescriptor, tool: ToolDefinition): number {
  const name = file.name.toLocaleLowerCase();
  const mime = file.type.toLocaleLowerCase();
  const extension = name.includes('.') ? `.${name.split('.').pop()}` : '';
  return (tool.accepts || []).reduce((score, rawAccept) => {
    const accept = rawAccept.toLocaleLowerCase();
    if (accept === mime || (extension && accept === extension)) return score + 60;
    if (accept.endsWith('/*') && mime.startsWith(accept.slice(0, -1))) return score + 45;
    if (extension && accept.includes(extension)) return score + 30;
    return score;
  }, 0);
}

function uniqueTools(items: readonly ToolDefinition[], limit: number): ToolDefinition[] {
  const seen = new Set<string>();
  const result: ToolDefinition[] = [];
  for (const tool of items) {
    const id = tool.parentToolId || tool.id;
    if (seen.has(id)) continue;
    seen.add(id);
    result.push(tool);
    if (result.length >= limit) break;
  }
  return result;
}
